<template>
  <div class="app-surface">
    <NavBar />

    <main class="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <section class="rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-soft md:p-6">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex items-center gap-4">
            <div class="grid h-16 w-16 place-items-center rounded-2xl bg-jackfruit-100 text-2xl font-black text-ink-900">
              {{ userInitials }}
            </div>
            <div>
              <div class="mb-1 inline-flex rounded-lg bg-jackfruit-100 px-3 py-1 text-xs font-black text-ink-700">个人中心</div>
              <h1 class="text-2xl font-black text-ink-900">{{ user?.nickname || '菠萝蜜读者' }}</h1>
              <p class="mt-1 text-sm text-ink-500">{{ user?.phone }}</p>
            </div>
          </div>
          <div class="grid grid-cols-3 gap-3 text-center sm:w-[26rem]">
            <div class="rounded-xl bg-jackfruit-50 px-4 py-3">
              <p class="text-xl font-black text-ink-900">{{ honeyBeanBalance }}</p>
              <p class="text-xs font-bold text-ink-500">蜜豆余额</p>
            </div>
            <div class="rounded-xl bg-pulp-50 px-4 py-3">
              <p class="text-xl font-black text-pulp-700">{{ creations.length }}</p>
              <p class="text-xs font-bold text-ink-500">创作作品</p>
            </div>
            <div class="rounded-xl bg-leaf-50 px-4 py-3">
              <p class="text-xl font-black text-leaf-700">{{ earningsSummary.totalEarned }}</p>
              <p class="text-xs font-bold text-ink-500">创作收益</p>
            </div>
          </div>
        </div>
      </section>

      <section class="mt-6 rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-soft md:p-6">
        <div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div class="mb-1 text-xs font-black uppercase text-leaf-700">Creator Earnings</div>
            <h2 class="text-xl font-black text-ink-900">创作者收益</h2>
            <p class="mt-1 text-sm text-ink-500">展示作品带来的蜜豆收益和阅读表现。</p>
          </div>
          <div class="grid grid-cols-3 gap-2 text-center">
            <div class="rounded-xl bg-jackfruit-50 px-3 py-2">
              <p class="text-lg font-black text-ink-900">{{ earningsSummary.totalEarned }}</p>
              <p class="text-[11px] font-bold text-ink-500">累计蜜豆</p>
            </div>
            <div class="rounded-xl bg-leaf-50 px-3 py-2">
              <p class="text-lg font-black text-leaf-700">{{ earningsSummary.settled }}</p>
              <p class="text-[11px] font-bold text-ink-500">已入账</p>
            </div>
            <div class="rounded-xl bg-amber-50 px-3 py-2">
              <p class="text-lg font-black text-amber-700">{{ earningsSummary.pending }}</p>
              <p class="text-[11px] font-bold text-ink-500">待结算</p>
            </div>
          </div>
        </div>

        <div v-if="isLoading" class="flex justify-center py-10">
          <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-jackfruit-500"></div>
        </div>

        <div v-else-if="earningsItems.length === 0" class="rounded-2xl bg-jackfruit-50 px-4 py-10 text-center">
          <h3 class="text-base font-black text-ink-900">还没有收益记录</h3>
          <p class="mt-2 text-sm text-ink-500">作品被读者付费解锁后，这里会按作品汇总展示。</p>
        </div>

        <div v-else class="grid gap-3 md:grid-cols-2">
          <article
            v-for="item in earningsItems"
            :key="item.book?.id || `missing-${item.honeyBeans}-${item.unlockCount}`"
            class="grid grid-cols-[64px_minmax(0,1fr)] gap-3 rounded-2xl border border-ink-100 bg-white p-3 shadow-sm"
          >
            <img
              v-if="item.book?.coverUrl"
              :src="item.book.coverUrl"
              :alt="item.book.title"
              class="aspect-[3/4] w-full rounded-xl object-cover"
            />
            <div v-else class="aspect-[3/4] rounded-xl bg-jackfruit-100"></div>
            <div class="min-w-0">
              <div class="mb-1 flex items-center justify-between gap-3">
                <h3 class="truncate text-sm font-black text-ink-900">{{ item.book?.title || '已下架作品' }}</h3>
                <span class="shrink-0 rounded-lg bg-jackfruit-100 px-2 py-1 text-xs font-black text-ink-900">
                  +{{ item.honeyBeans }} 蜜豆
                </span>
              </div>
              <div class="grid grid-cols-3 gap-2 text-xs font-bold text-ink-500">
                <span>{{ item.unlockCount }} 次解锁</span>
                <span>{{ item.book?.viewCount || 0 }} 阅读</span>
                <span>{{ item.book?.totalChapters || 0 }} 章</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section class="mt-6 rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-soft md:p-6">
        <div class="mb-5 flex items-center justify-between gap-4">
          <div>
            <div class="mb-1 text-xs font-black uppercase text-pulp-600">Creator Studio</div>
            <h2 class="text-xl font-black text-ink-900">我的创作</h2>
            <p class="mt-1 text-sm text-ink-500">你以创作者身份提交或上架的作品都会在这里。</p>
          </div>
          <router-link to="/bookshelf" class="hidden rounded-xl border border-jackfruit-200 px-4 py-2 text-sm font-black text-ink-600 hover:bg-jackfruit-50 sm:inline-flex">
            去书架
          </router-link>
        </div>

        <div v-if="isLoading" class="flex justify-center py-16">
          <div class="h-10 w-10 animate-spin rounded-full border-b-2 border-jackfruit-500"></div>
        </div>

        <div v-else-if="creations.length === 0" class="rounded-2xl bg-jackfruit-50 px-4 py-12 text-center">
          <div class="text-4xl">✍️</div>
          <h3 class="mt-3 text-lg font-black text-ink-900">还没有上架作品</h3>
          <p class="mt-2 text-sm text-ink-500">当你的私人分支达到创作者条件后，可以从阅读页提交上架审核。</p>
        </div>

        <div v-else class="grid gap-4 md:grid-cols-2">
          <article
            v-for="book in creations"
            :key="book.id"
            class="grid grid-cols-[88px_minmax(0,1fr)] gap-4 rounded-2xl border border-ink-100 bg-white p-3 shadow-sm"
          >
            <img
              :src="book.coverUrl"
              :alt="book.title"
              class="aspect-[3/4] w-full rounded-xl object-cover"
            />
            <div class="min-w-0">
              <div class="mb-2 flex flex-wrap items-center gap-2">
                <span class="rounded-lg px-2 py-1 text-[11px] font-black" :class="statusClass(book)">
                  {{ statusLabel(book) }}
                </span>
                <span class="rounded-lg bg-jackfruit-100 px-2 py-1 text-[11px] font-black text-ink-700">
                  {{ book.totalChapters }}章
                </span>
              </div>
              <h3 class="truncate text-base font-black text-ink-900">{{ book.title }}</h3>
              <p class="mt-1 line-clamp-2 text-xs leading-5 text-ink-500">{{ book.description }}</p>
              <p v-if="book.reviewReason" class="mt-2 line-clamp-2 rounded-lg bg-ink-50 px-2 py-1.5 text-[11px] font-bold text-ink-500">
                {{ book.reviewReason }}
              </p>
              <p v-if="book.appealStatus === 'pending'" class="mt-2 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] font-black text-amber-700">
                申诉已提交，等待人工复审
              </p>
              <div class="mt-3 flex items-center gap-2">
                <router-link
                  :to="`/books/${book.id}`"
                  class="rounded-lg bg-jackfruit-500 px-3 py-1.5 text-xs font-black text-ink-900"
                >
                  查看
                </router-link>
                <router-link
                  :to="`/reading/${book.id}/1`"
                  class="rounded-lg border border-jackfruit-200 px-3 py-1.5 text-xs font-black text-ink-600"
                >
                  阅读
                </router-link>
                <button
                  v-if="canAppeal(book)"
                  class="rounded-lg border border-red-100 bg-red-50 px-3 py-1.5 text-xs font-black text-red-600"
                  @click="openAppealDialog(book)"
                >
                  申诉
                </button>
              </div>
            </div>
          </article>
        </div>
      </section>
    </main>

    <div v-if="appealBook" class="fixed inset-0 z-[70] grid place-items-center bg-ink-900/40 px-4 backdrop-blur-sm" @click.self="appealBook = null">
      <div class="w-full max-w-md rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-fruit">
        <div class="mb-4">
          <div class="mb-1 text-xs font-black uppercase text-red-500">Appeal</div>
          <h2 class="text-lg font-black text-ink-900">提交申诉</h2>
          <p class="mt-1 text-sm leading-6 text-ink-500">说明为什么你认为《{{ appealBook.title }}》可以复审通过。</p>
        </div>
        <textarea
          v-model="appealReason"
          maxlength="500"
          rows="5"
          class="w-full resize-none rounded-xl border border-jackfruit-200 px-4 py-3 text-sm leading-6 text-ink-800 outline-none focus:border-jackfruit-400"
          placeholder="比如：被命中的词是在正常剧情语境中出现，没有露骨、煽动或歧视内容。"
        ></textarea>
        <div class="mt-2 flex justify-between text-xs text-ink-400">
          <span>至少 6 个字</span>
          <span>{{ appealReason.length }}/500</span>
        </div>
        <p v-if="appealError" class="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-bold text-red-600">{{ appealError }}</p>
        <div class="mt-5 flex justify-end gap-2">
          <button class="rounded-xl border border-jackfruit-200 px-4 py-2.5 text-sm font-black text-ink-600" @click="appealBook = null">
            取消
          </button>
          <button
            class="rounded-xl bg-jackfruit-500 px-4 py-2.5 text-sm font-black text-ink-900 shadow-fruit disabled:opacity-50"
            :disabled="isAppealing || appealReason.trim().length < 6"
            @click="submitAppeal"
          >
            {{ isAppealing ? '提交中...' : '提交申诉' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import NavBar from '@/components/NavBar.vue'
import { userApi } from '@/api'
import { useAuthStore } from '@/stores/auth'
import type { Book, CreatorEarningsByBook, CreatorEarningsSummary } from '@/types/user'

const authStore = useAuthStore()
const user = computed(() => authStore.user)
const userInitials = computed(() => user.value?.nickname?.charAt(0).toUpperCase() || '菠')
const honeyBeanBalance = computed(() => Math.floor(user.value?.balance || 0))

const isLoading = ref(true)
const creations = ref<Book[]>([])
const earningsSummary = ref<CreatorEarningsSummary>({
  totalEarned: 0,
  settled: 0,
  pending: 0
})
const earningsItems = ref<CreatorEarningsByBook[]>([])
const appealBook = ref<Book | null>(null)
const appealReason = ref('')
const appealError = ref('')
const isAppealing = ref(false)

const statusLabel = (book: Book) => {
  if (book.reviewStatus === 'pending' || book.status === 'pending_review') return '待审核'
  if (book.reviewStatus === 'rejected' || book.status === 'rejected') return '未通过'
  if (book.status === 'published') return '已上架'
  return '草稿'
}

const statusClass = (book: Book) => {
  if (book.reviewStatus === 'pending' || book.status === 'pending_review') return 'bg-amber-50 text-amber-700'
  if (book.reviewStatus === 'rejected' || book.status === 'rejected') return 'bg-red-50 text-red-600'
  if (book.status === 'published') return 'bg-leaf-50 text-leaf-700'
  return 'bg-ink-50 text-ink-500'
}

const canAppeal = (book: Book) => {
  return (book.reviewStatus === 'rejected' || book.status === 'rejected') && book.appealStatus !== 'pending'
}

const openAppealDialog = (book: Book) => {
  appealBook.value = book
  appealReason.value = ''
  appealError.value = ''
}

const submitAppeal = async () => {
  if (!appealBook.value || appealReason.value.trim().length < 6) return

  try {
    isAppealing.value = true
    appealError.value = ''
    const res = await userApi.appealCreation(appealBook.value.id, {
      reason: appealReason.value.trim()
    })

    if (res.code === 200 && res.data?.book) {
      creations.value = creations.value.map(book => book.id === res.data!.book.id ? res.data!.book : book)
      appealBook.value = null
    }
  } catch (err: any) {
    appealError.value = err.message || '申诉提交失败，请稍后再试'
  } finally {
    isAppealing.value = false
  }
}

const loadProfileData = async () => {
  isLoading.value = true
  try {
    const [creationsRes, earningsRes] = await Promise.all([
      userApi.getCreations({ limit: 50 }),
      userApi.getCreatorEarnings()
    ])

    if (creationsRes.code === 200 && creationsRes.data?.items) {
      creations.value = creationsRes.data.items
    }
    if (earningsRes.code === 200 && earningsRes.data) {
      earningsSummary.value = earningsRes.data.summary
      earningsItems.value = earningsRes.data.items
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(loadProfileData)
</script>
